import type { Theme } from '@mui/material/styles';

import DialogContent from '@mui/material/DialogContent';
import { useState, useRef } from 'react';
import Slider from 'react-slick';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import CustomMobileStepper from './CustomMobileStepper';
import Slide from './Slide';
import { useSlidesContent } from './SliderUtils';

import Dialog from '@egr/xbox/egr-gui-dialogs/utils/DialogOverride';
import { layoutInformation } from '@egr/xbox/utils/Layout';
import { makeStyles } from '@egr/xbox/utils/tss-react';

interface AboutSliderProps {
    open: boolean;
    handleClose: () => void;
}

const useStyles = makeStyles({ name: 'AboutSlider' })((theme: Theme) => ({
    dialogPaper: {
        maxWidth: 700,
        margin: 0,
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
        },
    },
    dialogContent: {
        padding: 0,
        flexDirection: 'column',
        display: 'flex',
    },
    sliderWrapper: {
        flexGrow: 1,
    },
}));

const AboutSlider: React.FC<AboutSliderProps> = ({ open, handleClose }) => {
    const [activeStep, setActiveStep] = useState(0);
    const sliderRef = useRef<Slider | null>(null);
    const { smallLayout } = layoutInformation.useState(['smallLayout']);
    const { classes } = useStyles();

    const slideContent = useSlidesContent();

    const handleNext = () => sliderRef.current?.slickNext();
    const handleBack = () => sliderRef.current?.slickPrev();
    const handleDialogClose = () => {
        setActiveStep(0);
        handleClose();
    };

    const settings = {
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        afterChange: (current: number) => setActiveStep(current),
        arrows: false,
    };

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            fullScreen={smallLayout}
            fullWidth
            classes={{ paper: classes.dialogPaper }}
        >
            <DialogContent className={classes.dialogContent}>
                <div className={classes.sliderWrapper}>
                    <Slider
                        {...settings}
                        ref={sliderRef}
                    >
                        {slideContent.map((step, index) => (
                            <Slide
                                key={index}
                                imgPath={step.imgPath}
                                title={step.title}
                                text={step.text}
                            />
                        ))}
                    </Slider>
                </div>
                <CustomMobileStepper
                    steps={slideContent.length}
                    activeStep={activeStep}
                    onBack={handleBack}
                    onNext={handleNext}
                    onClose={handleDialogClose}
                />
            </DialogContent>
        </Dialog>
    );
};

export default AboutSlider;
